import React, { useState, useRef, useEffect, ReactNode } from "react";
import { ArrowDownIcon, TickIcon } from "../../assets/icons";

export type SelectOption = {
  value: string;
  label: string;
  color?: string;
};

type SelectProps = {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: ReactNode;
  className?: string;
  buttonClassName?: string;
  defaultLabel?: string;
  showChevron?: boolean;
  selectedPrefix?: string;
  colorSelectedLabel?: boolean;
};

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  icon,
  className = "",
  buttonClassName = "",
  defaultLabel = "",
  showChevron = false,
  selectedPrefix = "",
  colorSelectedLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const defaultButtonClass =
    "cx-border cx-border-neutral-light cx-rounded-btn cx-px-3 cx-py-2 cx-text-sm cx-text-black cx-font-normal cx-w-fit cx-min-h-9 cx-bg-white cx-flex cx-items-center cx-justify-between cx-cursor-pointer";

  return (
    <div ref={containerRef} className={`cx-relative ${className}`}>
      <button
        type="button"
        className={buttonClassName || defaultButtonClass}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {icon && (
          <span className="cx-absolute cx-left-2 cx-top-1/2 -cx-translate-y-1/2">
            {icon}
          </span>
        )}
        <span
          className={`cx-flex cx-items-center cx-gap-1.5 ${
            selectedOption ? "" : "cx-text-neutral-medium"
          } ${icon ? "cx-pl-6" : ""}`}
        >
          {selectedOption ? (
            <>
              {selectedPrefix && <span>{selectedPrefix}</span>}
              <span
                style={
                  colorSelectedLabel && selectedOption.color
                    ? { color: selectedOption.color }
                    : undefined
                }
              >
                {selectedOption.label}
              </span>
            </>
          ) : (
            placeholder
          )}
        </span>
        {showChevron && (
          <ArrowDownIcon
            className={`cx-w-[12px] cx-h-[12px] cx-ml-3 cx-transition-transform cx-pt-[2px] ${
              isOpen ? "cx-rotate-180" : ""
            }`}
          />
        )}
      </button>
      {isOpen && (
        <ul className="cx-absolute cx-right-0 cx-z-50 cx-mt-1 cx-bg-white cx-border cx-border-neutral-light cx-rounded cx-shadow-lg cx-max-h-80 cx-w-56 cx-overflow-auto">
          <li
            className={`cx-px-3 cx-py-2 cx-text-sm cx-cursor-pointer hover:cx-bg-neutral-lightest cx-flex cx-items-center cx-justify-between ${
              value === "" ? "cx-bg-gray-100 cx-font-medium" : ""
            }`}
            onClick={() => handleSelect("")}
          >
            {defaultLabel || "All"}
            {value === "" && (
              <TickIcon height="100%" style={{ stroke: "#0064FF" }} />
            )}
          </li>
          {options.map((option) => (
            <li
              key={option.value}
              className={`cx-px-3 cx-py-2 cx-text-sm cx-cursor-pointer hover:cx-bg-neutral-lightest cx-flex cx-items-center cx-justify-between ${
                value === option.value ? "cx-bg-gray-100 cx-font-medium" : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              <span className="cx-flex cx-items-center cx-gap-2">
                {option.color && (
                  <span
                    className="cx-inline-block cx-w-2 cx-h-2 cx-rounded-full cx-flex-shrink-0"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                {option.label}
              </span>
              {value === option.value && (
                <TickIcon height="100%" style={{ stroke: "#0064FF" }} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Select;
